/**
 * @fileoverview Per-interaction-site nouns for BrazeAI Operator prose — the
 * single source each Operator-facing surface (composer instructions, page
 * context, tool descriptions) pulls its entity nouns from, so the prose can't
 * drift per call site. Plain strings (not t()) because Operator context and
 * tool descriptions are not user-visible UI — same convention as
 * `digestLabelByCategory` in scenes/AgentDetails/hooks/agentConsoleInstructions.ts.
 *
 * User-visible copy that varies by site does NOT belong here: the dashboard
 * convention is twin `-standards` translation keys chosen at the call site
 * (see AgentDetailsSegmentedControl), not a noun interpolated into a t()
 * sentence — and `I18nT<namespace>` would pin a getter here to one namespace.
 *
 * Being plain English is a coupling as well as a convention: `plural` is what
 * the Operator prose tells `click_menu_action` to match, but the Create-menu
 * items register their `data-operator-label` from `t()` values
 * (CreateAgentDropdown), so on a non-English workspace that match misses —
 * as it already did for the "Custom agent" / "Catalog Agent" labels.
 *
 * Uses an exhaustive Record so that adding a new interaction site constant
 * to shared_constants.json causes a compile-time error here, prompting the
 * developer to add the new entry.
 * @owner @Appboy/agent-console
 */

import type { InteractionSite } from "./types";
import { INTERACTION_SITES } from "./types";

export interface InteractionSiteOperatorNouns {
  /** What one of these is called, mid-sentence ("Canvas agent", "Campaign Standard"). */
  singular: string;
  /** Plural form, matching the Create-menu labels ("Canvas Standards"). */
  plural: string;
  /**
   * What this site's agent evaluates, as a plural noun ("campaigns",
   * "Canvases"); null for the prompt kinds, which generate output rather than
   * evaluate an object.
   */
  evaluates: string | null;
  /**
   * The wizard's step display names for this site, in order, matching the
   * step lists in AgentWizard.tsx (`stepIds`) and the English defaults of the
   * progress bar's `step-*-label` keys (AgentWizardProgressBar.tsx) — prompt
   * copy stays English even where the progress bar localizes. The i18n parser
   * requires a literal `t()` default, so the names can't share a constant;
   * the test checks them against the parsed English locale file instead.
   */
  wizardStepNames: readonly string[];
}

const canvasNouns: InteractionSiteOperatorNouns = {
  singular: "Canvas agent",
  plural: "Canvas agents",
  evaluates: null,
  wizardStepNames: ["Details", "Instructions", "Output"],
};

// A Record (rather than a per-call switch) keeps the returned objects
// referentially stable, so consumers may use them in hook dependency arrays;
// the Record type still makes a new interaction site a compile error here.
const nounsBySite: Record<InteractionSite, InteractionSiteOperatorNouns> = {
  [INTERACTION_SITES.CANVAS]: canvasNouns,
  [INTERACTION_SITES.CATALOG]: {
    singular: "Catalog agent",
    plural: "Catalog agents",
    evaluates: null,
    wizardStepNames: ["Details", "Instructions", "Output"],
  },
  [INTERACTION_SITES.CAMPAIGN_SETUP]: {
    singular: "Campaign Standard",
    plural: "Campaign Standards",
    evaluates: "campaigns",
    wizardStepNames: ["Details", "Campaign Rules"],
  },
  [INTERACTION_SITES.CANVAS_QA]: {
    singular: "Canvas Standard",
    plural: "Canvas Standards",
    evaluates: "Canvases",
    wizardStepNames: ["Details", "Canvas configuration", "Canvas steps"],
  },
};

/**
 * Returns the Operator-prose nouns for an interaction site.
 *
 * `undefined` resolves to the Canvas-agent nouns: the wizard renders the
 * Canvas setup by default while no site is in the URL or form yet, so prose
 * built before the site loads should describe what the user is looking at.
 */
export const interactionSiteOperatorNounsGetter = (
  site: InteractionSite | undefined
): InteractionSiteOperatorNouns => {
  if (site === undefined) {
    return canvasNouns;
  }
  return nounsBySite[site];
};
